import React, { useMemo, useRef, useEffect } from 'react'
import { pathOr, isNil } from 'ramda'
import PropTypes from 'prop-types'
import { Spin } from 'antd'
import { useQuery } from '@apollo/client'

import FACILITIES_PAGE from 'apollo/queries/FACILITIES_PAGE'
import FacilityProfileV2 from 'model/facilities/facilityProfileV2'
import { initArray } from 'model/helpers'

import { NODES_PATH } from 'constants/dataPaths'
import { verifiedValues } from 'components/VerifiedDropdown/helpers'
import NoResults from 'components/NoResults'
import { convertVerifiedAfterToSeconds } from 'utils/helpers/filtersHelper'
import useScroll from 'utils/hooks/useScroll'
import { TableWrap } from 'ui/entities/styled'

import FacilityItem from './FacilityItem'

const FacilitiesTable = ({
  filterValues,
  entitiesIDs = [],
  onSelectEntity = () => {},
  onRemoveEntity = () => {},
  isStandalonePage = false,
  isSelectionMode = false,
  addButtonText = false,
  setMarkers = () => {},
  setMarkersLoading = () => {},
  employee_id
}) => {
  const ref = useRef()
  const {
    address,
    name,
    location_types,
    is_verified,
    verified_after,
    distance,
    min_confidence,
    plan_id,
    insurance_plan_tiers
  } = filterValues

  const verifiedAfter = useMemo(() => {
    const date = verifiedValues.find(
      item => item.value === verified_after
    )?.subtract

    return convertVerifiedAfterToSeconds(date)
  }, [verified_after])

  const hasPlanId = !!plan_id
  const hasTiers = !!insurance_plan_tiers && !!insurance_plan_tiers?.length
  const distanceNum = distance ? parseInt(distance, 10) : 20
  const minConfidence = isNil(min_confidence)
    ? null
    : parseInt(min_confidence, 10)

  const variables = {
    member_has_plan: hasPlanId,
    ...(hasPlanId && { member_plan_id: plan_id }),
    employee_id: !!employee_id ? employee_id : undefined,
    filters: {
      min_confidence: minConfidence,
      address_in_one_line: address,
      confirmed: is_verified,
      confirmed_after: verifiedAfter,
      distance: !!address ? distanceNum : undefined,
      name,
      ...(location_types && { location_types: JSON.parse(location_types) }),
      ...(hasPlanId &&
        hasTiers && {
          insurance_plan_tiers: {
            kinds: JSON.parse(insurance_plan_tiers),
            plan_id
          }
        })
    }
  }

  const { data, loading, fetchMore } = useQuery(FACILITIES_PAGE, {
    variables,
    errorPolicy: 'all'
  })
  const pageInfo = pathOr({}, ['facilities_v2', 'pageInfo'], data)
  const dataPath = ['facilities_v2']
  const facilities = initArray(
    data,
    [...dataPath, NODES_PATH],
    FacilityProfileV2
  )

  const { scrollLoading } = useScroll({
    dataPath,
    dataType: NODES_PATH,
    fetchMore,
    pageInfo,
    scrollRef: ref,
    filterUniqueIds: true
  })

  useEffect(() => {
    setMarkersLoading(loading)
    if (facilities)
      setMarkers(
        facilities.map(
          // eslint-disable-next-line
          ({ id, google_maps_link, latitude, longitude, name }) => ({
            id,
            google_maps_link,
            latitude,
            longitude,
            name
          })
        )
      )
    // eslint-disable-next-line
  }, [loading, JSON.stringify(facilities), setMarkers, setMarkersLoading])

  return (
    <Spin spinning={loading || scrollLoading}>
      <TableWrap ref={ref} customOffset={!isStandalonePage ? '530' : 'false'}>
        {facilities.map((facility, index) => (
          <FacilityItem
            key={facility.id}
            facility={facility}
            index={index + 1}
            addButtonText={addButtonText}
            isSelectionMode={isSelectionMode}
            isSelected={
              entitiesIDs.includes(facility.id) ||
              entitiesIDs.includes(facility.uuid)
            }
            onSelectEntity={onSelectEntity}
            onRemoveEntity={onRemoveEntity}
          />
        ))}
        {facilities.length === 0 && !loading && <NoResults />}
      </TableWrap>
    </Spin>
  )
}

FacilitiesTable.propTypes = {
  filterValues: PropTypes.object.isRequired,
  onSelectEntity: PropTypes.func,
  onRemoveEntity: PropTypes.func,
  entitiesIDs: PropTypes.array,
  isStandalonePage: PropTypes.bool,
  isSelectionMode: PropTypes.bool,
  addButtonText: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  setMarkers: PropTypes.func,
  setMarkersLoading: PropTypes.func
}

export default FacilitiesTable
